import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { installerBranding } from "@/branding/Branding";
import { PASSWORD_INPUT_PROPS } from "@/utils/passwordInputProps";
import { toast } from "sonner";

/**
 * `/reset-password?token=…` — public. Uses the single-use, 30-minute token
 * issued by /api/auth/forgot-password. The backend re-runs the full
 * password policy, so we mirror its rules locally for a live checklist
 * and strength indicator (never a substitute for server-side validation).
 */
const REQUIREMENTS = [
    { id: "len",  label: "At least 12 characters",             test: (p) => p.length >= 12 },
    { id: "mix",  label: "Mix of upper + lower case letters",  test: (p) => /[a-z]/.test(p) && /[A-Z]/.test(p) },
    { id: "num",  label: "Contains a number",                  test: (p) => /[0-9]/.test(p) },
    { id: "spec", label: "Contains a special character",       test: (p) => /[^A-Za-z0-9]/.test(p) },
];

function strengthOf(p) {
    if (!p) return 0;
    let score = 0;
    if (p.length >= 12) score++;
    if (p.length >= 16) score++;
    if (/[a-z]/.test(p) && /[A-Z]/.test(p)) score++;
    if (/[0-9]/.test(p) && /[^A-Za-z0-9]/.test(p)) score++;
    return Math.min(4, score);
}

export default function ResetPassword() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const [pw, setPw] = useState("");
    const [confirm, setConfirm] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [expired, setExpired] = useState(!token);
    const navigate = useNavigate();

    const score = useMemo(() => strengthOf(pw), [pw]);
    const strengthLabel = ["Too short", "Weak", "Okay", "Strong", "Excellent"][score];
    const strengthCls   = ["bg-[#DA3535]", "bg-[#F59E0B]", "bg-[#F59E0B]", "bg-[#3E9E2E]", "bg-[#2E7E22]"][score];

    async function onSubmit(e) {
        e.preventDefault();
        if (pw !== confirm) { setError("Passwords don't match."); return; }
        setBusy(true); setError("");
        try {
            await api.resetPassword(token, pw, confirm);
            toast.success("Password updated — please sign in with your new password.");
            navigate("/");
        } catch (err) {
            const msg = formatApiError(err?.response?.data?.detail) || "Reset failed.";
            setError(msg);
            if (err?.response?.status === 400) setExpired(true);
        } finally { setBusy(false); }
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#F5F5F7] px-4">
            <div className="w-full max-w-md bg-white border border-[#E5E7EB] rounded-2xl p-6 md:p-8">
                <img src={installerBranding.logoUrl} alt={installerBranding.name} className="h-10 w-auto mb-6" />
                <h1 className="text-2xl text-[#131B2F] font-medium">Choose a new password</h1>

                {expired ? (
                    <>
                        <p className="mt-2 text-sm text-[#4B5565]" data-testid="reset-expired">
                            This reset link is invalid or has expired. Request a new one to continue.
                        </p>
                        <div className="mt-6 flex gap-3">
                            <Link to="/forgot-password" data-testid="reset-request-new"
                                  className="inline-flex items-center justify-center h-11 px-4 rounded-md bg-[#3E9E2E] hover:bg-[#2E7E22] text-white font-medium">
                                Request a new link
                            </Link>
                            <Link to="/" data-testid="reset-back-to-login"
                                  className="inline-flex items-center justify-center h-11 px-4 rounded-md border border-[#E5E7EB] text-[#131B2F] hover:bg-[#F5F5F7]">
                                Back to sign in
                            </Link>
                        </div>
                    </>
                ) : (
                    <form onSubmit={onSubmit} className="mt-5" data-testid="reset-form">
                        <label className="block text-sm text-[#4B5565] mb-1.5">New password</label>
                        <div className="relative">
                            <input
                                data-testid="reset-password-input"
                                type={showPw ? "text" : "password"} required minLength={12} value={pw}
                                onChange={(e) => setPw(e.target.value)} autoComplete="new-password"
                                {...PASSWORD_INPUT_PROPS}
                                className="w-full h-11 rounded-md border border-[#E5E7EB] bg-white px-3.5 pr-16 text-base outline-none focus:border-[#3E9E2E]"
                            />
                            <button
                                type="button" onClick={() => setShowPw((v) => !v)}
                                data-testid="reset-password-toggle"
                                className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 text-xs text-[#4B5565] hover:text-[#131B2F]"
                                aria-label={showPw ? "Hide password" : "Show password"}
                            >{showPw ? "Hide" : "Show"}</button>
                        </div>

                        <label className="block text-sm text-[#4B5565] mt-4 mb-1.5">Confirm new password</label>
                        <input
                            data-testid="reset-confirm-input"
                            type={showPw ? "text" : "password"} required minLength={12} value={confirm}
                            onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password"
                            {...PASSWORD_INPUT_PROPS}
                            className="w-full h-11 rounded-md border border-[#E5E7EB] bg-white px-3.5 text-base outline-none focus:border-[#3E9E2E]"
                        />

                        {/* Strength indicator (visual hint only — server still validates) */}
                        <div className="mt-3" data-testid="reset-strength">
                            <div className="flex gap-1 h-1.5">
                                {[0, 1, 2, 3].map((i) => (
                                    <div key={i} className={`flex-1 rounded ${i < score ? strengthCls : "bg-[#E5E7EB]"}`} />
                                ))}
                            </div>
                            <div className="mt-1 text-xs text-[#4B5565]">{pw ? strengthLabel : "Enter a password"}</div>
                        </div>

                        <ul className="mt-4 space-y-1" data-testid="reset-checklist">
                            {REQUIREMENTS.map((r) => {
                                const ok = r.test(pw);
                                return (
                                    <li key={r.id} className={`text-xs flex items-center gap-2 ${ok ? "text-[#2E7E22]" : "text-[#4B5565]"}`}>
                                        <span className={`w-3 h-3 rounded-full ${ok ? "bg-[#3E9E2E]" : "bg-[#E5E7EB]"}`} />
                                        {r.label}
                                    </li>
                                );
                            })}
                        </ul>

                        {error && <div className="mt-3 text-sm text-[#DA3535]" data-testid="reset-error">{error}</div>}

                        <button
                            type="submit" disabled={busy || pw.length < 12 || pw !== confirm}
                            data-testid="reset-submit"
                            className="mt-5 w-full h-11 rounded-md bg-[#3E9E2E] hover:bg-[#2E7E22] disabled:opacity-70 text-white text-base font-medium"
                        >{busy ? "Updating…" : "Set new password"}</button>
                    </form>
                )}
            </div>
        </div>
    );
}
